import { useEffect } from "react";
import {
	Routes,
	Route,
	Navigate,
	useLocation,
	useNavigate
} from "react-router-dom";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Sidenav from "examples/Sidenav";
import SidenavHeader from "components/DigiHapi/SidenavHeader";
import WithRouterProps from "components/DigiHapi/WithRouterProps";
import { useMaterialUIController, setMiniSidenav } from "context";

// core components
import Footer from "components/Footer/Footer.js";

import routesWebmaster from "routesWebmaster";
import routesAdministrator from "routesAdmin";
import routesEmployee from "routesEmployee";

import { fetchGetUser } from "actions/profile";
import { fetchLogout } from "actions/login";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
	ROLE_WEBMASTER,
	ROLE_ADMINISTRATOR,
	ROLE_EMPLOYEE
} from "actions/types";
import { getImage } from "assets/Images";

// Convert the admin routes definition into the format expected by the Sidenav
const toSidenavRoutes = (routes, nested = false) =>
	routes
		.filter(route => !route.invisible)
		.map(route => {
			const RouteIcon = route.icon;
			const icon =
				!nested && RouteIcon ? (
					<RouteIcon style={{ fontSize: "1.25rem" }} />
				) : undefined;
			if (route.collapse) {
				return {
					type: "collapse",
					name: route.name,
					key: route.state,
					icon,
					collapse: toSidenavRoutes(route.views, true)
				};
			}
			const path = route.layout + route.path;
			return {
				type: "collapse",
				name: route.name,
				key: path,
				icon,
				route: path,
				noCollapse: true
			};
		});

export default function Admin() {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	// Layout of the current URL: "/team", "/admin" or "/webmaster"
	const base = `/${pathname.split("/")[1]}`;

	const dispatch = useDispatch();
	const [controller, uiDispatch] = useMaterialUIController();
	const { sidenavColor } = controller;

	const { user } = useSelector(state => state.profileReducer);
	const { logoutResult } = useSelector(state => state.loginReducer);
	const { community, organization } = useSelector(
		state => state.entitiesReducer
	);

	useEffect(() => {
		if (!user) {
			dispatch(fetchGetUser());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (logoutResult) {
			navigate("/");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [logoutResult]);

	useEffect(() => {
		if (pathname) {
			document.documentElement.scrollTop = 0;
			if (document.scrollingElement) {
				document.scrollingElement.scrollTop = 0;
			}
			// Close the sidenav on small screens after navigating
			if (window.innerWidth < 1200) {
				setMiniSidenav(uiDispatch, true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	const getActiveRoute = routes => {
		let activeRoute = "Default Brand Text";
		for (let i = 0; i < routes.length; i++) {
			if (routes[i].collapse) {
				let collapseActiveRoute = getActiveRoute(routes[i].views);
				if (collapseActiveRoute !== activeRoute) {
					return collapseActiveRoute;
				}
			} else {
				if (
					window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
				) {
					return routes[i].name;
				}
			}
		}
		return activeRoute;
	};

	const isLayoutAllowed = layout =>
		(layout === "/team" && user.role === ROLE_EMPLOYEE) ||
		((layout === "/team" || layout === "/admin") &&
			user.role === ROLE_ADMINISTRATOR) ||
		((layout === "/team" || layout === "/admin" || layout === "/webmaster") &&
			user.role === ROLE_WEBMASTER);

	// Routes of the current layout, relative to it (descendant routes of "/<layout>/*")
	const getRoutes = routes => {
		return routes.map((prop, key) => {
			if (prop.collapse) {
				return getRoutes(prop.views);
			}
			if (user) {
				if (isLayoutAllowed(prop.layout)) {
					return prop.layout === base ? (
						<Route
							path={`${prop.path.replace(/^\//, "")}/*`}
							element={<WithRouterProps component={prop.component} />}
							key={key}
						/>
					) : null;
				} else {
					dispatch(fetchLogout());
					return null;
				}
			} else {
				return null;
			}
		});
	};

	// Unknown URLs fall back to the community page (employees: only under /team)
	const shouldRedirect =
		user &&
		(base === "/team" ||
			user.role === ROLE_ADMINISTRATOR ||
			user.role === ROLE_WEBMASTER);

	let myRoutes = routesEmployee({ community, organization });

	if (user) {
		switch (user.role) {
			case ROLE_WEBMASTER:
				myRoutes = routesWebmaster({ community, organization });
				break;
			case ROLE_ADMINISTRATOR:
				myRoutes = routesAdministrator({ community, organization });
				break;
			default:
				// Employee
				break;
		}
	}

	const activeRoute = getActiveRoute(myRoutes);

	return (
		<>
			<Sidenav
				color={sidenavColor}
				brand={getImage("logorounded")}
				brandName="Administration"
				brandRoute="/team/about"
				header={<SidenavHeader />}
				routes={toSidenavRoutes(myRoutes)}
			/>
			<DashboardLayout>
				{/* Full-height column so that the footer sticks to the bottom of the screen
				    (DashboardLayout already has a 24px padding on each side) */}
				<MDBox
					display="flex"
					flexDirection="column"
					minHeight="calc(100vh - 48px)"
				>
					<DashboardNavbar
						title={activeRoute !== "Default Brand Text" ? activeRoute : ""}
					/>
					<MDBox py={3} flexGrow={1}>
						<Routes>
							{getRoutes(myRoutes)}
							<Route
								path="*"
								element={
									shouldRedirect ? (
										<Navigate to="/team/community" replace />
									) : null
								}
							/>
						</Routes>
					</MDBox>
					<Footer />
				</MDBox>
			</DashboardLayout>
		</>
	);
}
